/**
 * Excel-Export für Prüfberichte. Baut aus dem `NormalizedReport` (siehe
 * `normalized-report.model.ts`) eine Arbeitsmappe im Stil der BIC-CI (siehe `ci-colors.ts`):
 * ein Tabellenblatt je geprüfter Datei.
 *
 * IDS hat eine eigene Achsen-Aufteilung (Spalten = Spezifikationen, Zeilen = Anforderungen,
 * siehe {@link buildIdsReportWorkbook}). Hybrid- und KI-Prüfberichte werden stattdessen als flache
 * Tabelle exportiert (eine Zeile je Prüfschritt, Prüfregel-Spalten über mehrere Zeilen verschmolzen,
 * siehe {@link buildKiHybridReportWorkbook}) – dort passen Spezifikationsspalten nicht, weil die
 * Anzahl der Prüfschritte je Regel stark variiert. {@link downloadCheckReportExcel} wählt anhand des
 * Prüfungstyps den passenden Builder.
 */
import ExcelJS from 'exceljs';
import {NormalizedReport, ReportDetail, ReportGroup, ReportRule, ReportStructure} from './normalized-report.model';
import {IdsThresholds, statusFromIdsThresholds} from './ids-thresholds';
import {CI_COLORS} from './ci-colors';
import {ReportDisplayConfig, ResolvedDetailEntry, resolveDetailEntries} from './report-display-config';
import {DetailColumn, resolveDetailColumns} from './report-detail-blocks';
import {reportExportFileName, reportTypeLabel, reportTypeSubtitle} from './report-export-labels';
import {BIC_LOGO_HEIGHT, BIC_LOGO_WHITE_PNG_BASE64, BIC_LOGO_WIDTH} from './bic-logo';

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const THIN_BORDER_SIDE: ExcelJS.Border = {style: 'thin', color: {argb: CI_COLORS.darkText}};
const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: THIN_BORDER_SIDE, left: THIN_BORDER_SIDE, bottom: THIN_BORDER_SIDE, right: THIN_BORDER_SIDE,
};
type ReportStatus = 'success' | 'warning' | 'error' | 'unknown';

/** BIC-Logo als Bild im Workbook registrieren (einmal je Arbeitsmappe, dann in jedem Blatt platziert). */
function registerLogoImage(workbook: ExcelJS.Workbook): number {
  return workbook.addImage({base64: BIC_LOGO_WHITE_PNG_BASE64, extension: 'png'});
}

/**
 * Platziert das Logo rechts in der dunkelblauen Titelzeile (Zeile 1). Über eine feste Pixelgröße
 * statt Spaltenbreiten verankert, damit es unabhängig von der Spaltenanzahl gleich groß bleibt; der
 * Anker in der letzten Spalte hält es vom links stehenden Titeltext fern.
 */
function placeLogoInTitleRow(sheet: ExcelJS.Worksheet, columnCount: number, imageId: number): void {
  const height = 36;
  const width = Math.round(height * (BIC_LOGO_WIDTH / BIC_LOGO_HEIGHT));
  sheet.addImage(imageId, {
    tl: {col: Math.max(columnCount - 1, 0) + 0.15, row: 0.1},
    ext: {width, height},
  });
}

export async function buildIdsReportWorkbook(
  report: NormalizedReport, idsThresholds?: IdsThresholds | null,
): Promise<ExcelJS.Workbook> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'planBIC';
  workbook.created = report.meta.checkDate;

  const imageId = registerLogoImage(workbook);
  const usedSheetNames = new Set<string>();
  for (const group of report.groups) {
    addIdsSheet(workbook, report, group, imageId, usedSheetNames, idsThresholds);
  }
  return workbook;
}

export async function downloadIdsExcelReport(
  report: NormalizedReport, idsThresholds?: IdsThresholds | null, runName?: string | null,
): Promise<void> {
  const workbook = await buildIdsReportWorkbook(report, idsThresholds);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {type: EXCEL_MIME_TYPE});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = reportExportFileName('IDS', runName, report.meta.rulesetName, report.meta.checkDate, 'xlsx');
  link.click();
  URL.revokeObjectURL(url);
}

function addIdsSheet(
  workbook: ExcelJS.Workbook, report: NormalizedReport, group: ReportGroup, imageId: number,
  usedSheetNames: Set<string>,
  idsThresholds?: IdsThresholds | null,
): void {
  const sheet = workbook.addWorksheet(sanitizeSheetName(group.name, usedSheetNames));
  sheet.views = [{showGridLines: false}];
  const specs = group.rules;
  const requirementLabels = collectRequirementLabels(specs);
  const columnCount = Math.max(specs.length + 1, 4);

  writeTitleBlock(sheet, columnCount);
  placeLogoInTitleRow(sheet, columnCount, imageId);
  const metaRowCount = writeMetaBlock(sheet, report, group, specs.length);

  // Leerzeile zwischen den Berichtsangaben und der Ergebnistabelle.
  const headerRow = 4 + metaRowCount + 1;
  writeSpecHeaderRow(sheet, headerRow, specs);
  writeCountRow(sheet, headerRow + 1, specs);
  writeAggregateResultRow(sheet, headerRow + 2, specs, idsThresholds);

  let lastRow = headerRow + 2;
  if (requirementLabels.length === 0) {
    lastRow = headerRow + 3;
    sheet.mergeCells(lastRow, 1, lastRow, columnCount);
    const cell = sheet.getCell(lastRow, 1);
    cell.value = 'Keine Anforderungen vorhanden.';
    cell.font = {name: 'Segoe UI', size: 10, italic: true, color: {argb: CI_COLORS.darkText}};
  } else {
    requirementLabels.forEach((label, idx) =>
      writeRequirementRow(sheet, headerRow + 3 + idx, label, specs, idsThresholds));
    lastRow = headerRow + 2 + requirementLabels.length;
  }

  sheet.getColumn(1).width = 32;
  specs.forEach((_, i) => sheet.getColumn(i + 2).width = 6.5);

  setupPrintLayout(sheet, lastRow, specs.length + 1);
}

function writeTitleBlock(sheet: ExcelJS.Worksheet, columnCount: number): void {
  sheet.mergeCells(1, 1, 1, columnCount);
  const title = sheet.getCell(1, 1);
  title.value = 'IDS-Bericht';
  title.font = {name: 'Segoe UI', size: 20, bold: true, color: {argb: CI_COLORS.white}};
  title.fill = solidFill(CI_COLORS.darkBlue);
  title.alignment = {horizontal: 'left', vertical: 'middle'};
  sheet.getRow(1).height = 30;

  sheet.mergeCells(2, 1, 2, columnCount);
  const subtitle = sheet.getCell(2, 1);
  subtitle.value = 'Überprüfung des BIM-Modells gegen die Information Delivery Specification (IDS)';
  subtitle.font = {name: 'Segoe UI', size: 10, italic: true, color: {argb: CI_COLORS.darkBlue}};
  subtitle.fill = solidFill(CI_COLORS.turquoiseLight);
  subtitle.alignment = {horizontal: 'left', vertical: 'middle'};
  sheet.getRow(2).height = 18;
}

/**
 * Berichtsangaben über der Ergebnistabelle.
 *
 * @returns Anzahl der geschriebenen Zeilen — die Ergebnistabelle beginnt darunter. Vorher stand
 *   deren Startzeile als Konstante im Code und wäre bei einer zusätzlichen Angabe überschrieben
 *   worden.
 */
function writeMetaBlock(
  sheet: ExcelJS.Worksheet, report: NormalizedReport, group: ReportGroup, specCount: number,
): number {
  // Jedes Blatt ist EINE Datei, "Gesamtergebnis" war hier also zweideutig (#2317): gemeint war
  // immer das Ergebnis dieses Modells. Bei mehreren Dateien steht das echte Gesamtergebnis des
  // Laufs zusätzlich darunter — vorher war es im Excel überhaupt nicht zu finden.
  //
  // Bei genau einer Datei IST "Ergebnis dieses Modells" das Gesamtergebnis des Laufs - dafür wird
  // hier bewusst `report.meta.score` verwendet und nicht `group.percent`: `meta.score` ist die
  // EINE Quelle für Kreis/PDF/Excel (siehe idsOverallPercent in check-report-page.component.ts),
  // `group.percent` mittelt dagegen nur die *angezeigten* Spezifikationswerte und kann durch die
  // doppelte Rundung leicht abweichen (z. B. 67 %/60 % ⇒ 63,5 ⇒ 64 % statt der 63 % aus meta.score).
  const isMultiFile = report.groups.length > 1;
  const metaRows: Array<{ label: string; value: string | number; numFmt?: string }> = [
    {label: 'Prüfung durchgeführt am:', value: formatDate(report.meta.checkDate)},
    {label: 'Prüfregelname:', value: report.meta.rulesetName || '–'},
    {label: 'Geprüftes Modell:', value: group.name || '–'},
    {label: 'Geprüfte Spezifikationen:', value: specCount},
    {
      label: isMultiFile ? 'Ergebnis dieses Modells:' : 'Gesamtergebnis:',
      ...percentCellValue(isMultiFile ? group.percent : report.meta.score),
    },
    ...(isMultiFile
      ? [{
        label: `Gesamtergebnis (${report.groups.length} Dateien):`,
        ...percentCellValue(report.meta.score),
      }]
      : []),
  ];

  metaRows.forEach((row, idx) => {
    const rowIdx = 4 + idx;
    const labelCell = sheet.getCell(rowIdx, 1);
    labelCell.value = row.label;
    labelCell.font = {name: 'Segoe UI', size: 10, color: {argb: CI_COLORS.darkText}};

    const valueCell = sheet.getCell(rowIdx, 2);
    valueCell.value = row.value;
    valueCell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: CI_COLORS.darkText}};
    // Explizit links ausrichten: Zahlen (Spezifikationen, Gesamtergebnis-%) würden sonst rechts
    // ausgerichtet, während die Textwerte darüber links stehen – uneinheitliches Bild.
    valueCell.alignment = {horizontal: 'left', vertical: 'middle'};
    if (row.numFmt) valueCell.numFmt = row.numFmt;
  });

  return metaRows.length;
}

function writeSpecHeaderRow(sheet: ExcelJS.Worksheet, rowIdx: number, specs: ReportRule[]): void {
  // Erste Spalte der Kopfzeile bleibt ohne Beschriftung, erhält aber denselben Rahmen wie die
  // übrigen Kopfzellen, damit der äußere Rahmen der Tabelle durchgängig geschlossen ist.
  styleHeaderCell(sheet.getCell(rowIdx, 1), false);

  specs.forEach((spec, i) => {
    const cell = sheet.getCell(rowIdx, i + 2);
    cell.value = spec.notApplicable ? `${spec.name} (nicht zutreffend)` : spec.name;
    styleHeaderCell(cell, true);
  });
  sheet.getRow(rowIdx).height = 110;
}

function writeCountRow(sheet: ExcelJS.Worksheet, rowIdx: number, specs: ReportRule[]): void {
  const labelCell = sheet.getCell(rowIdx, 1);
  labelCell.value = 'Geprüfte Bauteile';
  styleLabelCell(labelCell, false);

  specs.forEach((spec, i) => {
    const cell = sheet.getCell(rowIdx, i + 2);
    cell.value = spec.elementCount ?? 0;
    cell.font = {name: 'Segoe UI', size: 10, color: {argb: CI_COLORS.darkText}};
    cell.alignment = {horizontal: 'center', vertical: 'middle'};
    cell.border = THIN_BORDER;
  });
}

function writeAggregateResultRow(
  sheet: ExcelJS.Worksheet, rowIdx: number, specs: ReportRule[],
  idsThresholds?: IdsThresholds | null,
): void {
  const labelCell = sheet.getCell(rowIdx, 1);
  labelCell.value = 'Gesamtergebnis der Spezifikation';
  styleLabelCell(labelCell, true);

  specs.forEach((spec, i) => setPercentCell(sheet.getCell(rowIdx, i + 2), spec.percent, idsThresholds));
}

function writeRequirementRow(
  sheet: ExcelJS.Worksheet, rowIdx: number, label: string, specs: ReportRule[],
  idsThresholds?: IdsThresholds | null,
): void {
  const labelCell = sheet.getCell(rowIdx, 1);
  labelCell.value = label;
  styleLabelCell(labelCell, false);

  specs.forEach((spec, i) => {
    const cell = sheet.getCell(rowIdx, i + 2);
    const detail = spec.details.find(d => (d.text || requirementLabel(d)) === label);
    if (detail) {
      setPercentCell(cell, detail.percent ?? 0, idsThresholds);
    } else {
      cell.value = null;
      cell.fill = solidFill(CI_COLORS.lightGray);
      cell.border = THIN_BORDER;
    }
  });
}

/** Sammelt alle Anforderungs-Labels über alle Spezifikationen, in Reihenfolge des ersten Auftretens. */
function collectRequirementLabels(specs: ReportRule[]): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const spec of specs) {
    for (const detail of spec.details) {
      const label = detail.text || requirementLabel(detail);
      if (label && !seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    }
  }
  return labels;
}

function requirementLabel(detail: { facetLabel?: string; name?: string }): string {
  return [detail.facetLabel, detail.name].filter(Boolean).join(': ') || 'Anforderung';
}

/**
 * Ergebniszelle des IDS-Blatts.
 *
 * Sind Schwellwerte konfiguriert (#2309), wird dreistufig gefärbt — vorher kannte dieser Export
 * gar kein Gelb. Ohne Konfiguration bleibt es bei der bisherigen harten 100-%-Grenze; eine
 * Umstellung auf den Berichtsstatus wäre nicht deckungsgleich, weil der IDS-Status ein Boolean ist
 * und ein auf 100 % gerundetes Ergebnis von 99,6 % dort als Fehler gilt.
 */
function setPercentCell(
  cell: ExcelJS.Cell, percent: number | null | undefined, thresholds?: IdsThresholds | null,
): void {
  if (percent == null) {
    setUnratedCell(cell);
    return;
  }
  const clamped = clampPercent(percent);
  cell.value = clamped / 100;
  cell.numFmt = '0%';
  cell.font = {
    name: 'Segoe UI', size: 10, bold: true,
    color: {
      argb: thresholds
        ? statusFontColor(statusFromIdsThresholds(clamped, thresholds))
        : clamped >= 100 ? CI_COLORS.turquoise : CI_COLORS.red,
    },
  };
  cell.alignment = {horizontal: 'center', vertical: 'middle'};
  cell.border = THIN_BORDER;
}

/** Kopfzeile der Ergebnistabelle wie in der alten Vorlage: kein Farbfüllung, nur Rahmen. */
function styleHeaderCell(cell: ExcelJS.Cell, rotate: boolean): void {
  cell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: CI_COLORS.darkText}};
  cell.fill = solidFill(CI_COLORS.white);
  cell.alignment = rotate
    ? {horizontal: 'center', vertical: 'bottom', textRotation: 90, wrapText: true}
    : {horizontal: 'left', vertical: 'middle'};
  cell.border = THIN_BORDER;
}

/** Druckbereich auf die Tabelle begrenzt, Breite an eine Seite angepasst (Querformat für breite Tabellen). */
function setupPrintLayout(sheet: ExcelJS.Worksheet, lastRow: number, lastCol: number): void {
  const lastColLetter = sheet.getColumn(lastCol).letter;
  sheet.pageSetup.printArea = `A1:${lastColLetter}${lastRow}`;
  sheet.pageSetup.orientation = 'landscape';
  sheet.pageSetup.fitToPage = true;
  sheet.pageSetup.fitToWidth = 1;
  sheet.pageSetup.fitToHeight = 0;
  sheet.pageSetup.showGridLines = false;
}

function styleLabelCell(cell: ExcelJS.Cell, bold: boolean): void {
  cell.font = {name: 'Segoe UI', size: 10, bold, color: {argb: CI_COLORS.darkText}};
  cell.alignment = {horizontal: 'left', vertical: 'middle', wrapText: true};
  cell.border = THIN_BORDER;
}

function solidFill(argb: string): ExcelJS.Fill {
  return {type: 'pattern', pattern: 'solid', fgColor: {argb}};
}

function clampPercent(percent: number | null | undefined): number {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) return 0;
  return Math.max(0, Math.min(percent, 100));
}

/**
 * Zellwert für ein Ergebnis in Prozent – oder „–", wenn nichts bewertet wurde.
 *
 * Als 0 % ausgegeben wäre ein unbewertetes Ergebnis im Export von „nicht erfüllt" nicht zu
 * unterscheiden; genau diese Verwechslung soll der Bericht nicht mehr anbieten.
 */
function percentCellValue(percent: number | null | undefined): { value: string | number; numFmt?: string } {
  return percent == null
    ? {value: '–'}
    : {value: clampPercent(percent) / 100, numFmt: '0%'};
}

function formatDate(date: Date): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '–';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

/** Excel-Tabellenblattnamen: max. 31 Zeichen, keine `\ / ? * [ ]`, eindeutig innerhalb der Mappe. */
function sanitizeSheetName(name: string, usedNames: Set<string>): string {
  const base = sanitizeFileNamePart(name || 'Bericht', 31) || 'Bericht';
  let candidate = base;
  let suffix = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const marker = ` (${suffix})`;
    candidate = base.slice(0, 31 - marker.length) + marker;
    suffix++;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

function sanitizeFileNamePart(name: string, maxLength = 150): string {
  return name.replace(/[\\/?*[\]:]/g, '-').trim().slice(0, maxLength);
}

// ─── Hybrid / KI ──────────────────────────────────────────────────────────────
//
// Flache Tabelle statt Spezifikationsspalten (siehe Dateikopf): eine Zeile je Prüfschritt,
// die Prüfregel-Spalten (Name/Kürzel/Beschreibung/Ergebnis/Status) sind über ihre Prüfschritt-
// Zeilen verschmolzen (Master-Detail-Optik), die Positions-Spalte (Hybrid) zusätzlich über ihre
// Prüfregeln. So bleibt jede Zeile für sich vollständig auswertbar (Filtern/Sortieren in Excel),
// ohne dass Regel-/Positionsangaben optisch wiederholt wirken.

/** Wählt anhand des Prüfungstyps den passenden Excel-Export-Builder. */
export async function downloadCheckReportExcel(
  report: NormalizedReport,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
  idsThresholds?: IdsThresholds | null,
  runName?: string | null,
): Promise<void> {
  if ((checkType ?? '').toUpperCase() === 'IDS') {
    await downloadIdsExcelReport(report, idsThresholds, runName);
  } else {
    await downloadKiHybridExcelReport(report, config, checkType, runName);
  }
}

export async function buildKiHybridReportWorkbook(
  report: NormalizedReport,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
): Promise<ExcelJS.Workbook> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'planBIC';
  workbook.created = report.meta.checkDate;

  // Spaltenplanung über den ganzen Bericht (nicht je Datei einzeln), damit alle Tabellenblätter
  // dieselben Spalten haben – sonst entstehen leere Spalten (z. B. für Verdikt-Felder wie
  // "Ergebnis"/"Status", die laut Datenmodell nie als Text dargestellt werden, siehe
  // VERDICT_COLUMN_RE/STATUS_COLUMN_RE in report-display-config.ts).
  const allRules = report.groups.flatMap(allRulesOf);
  const detailColumns = pruneEmptyDetailColumns(allRules, resolveGenericDetailColumns(allRules, config));
  const showCountColumn = reportHasElementCounts(allRules);
  const showStepPercentColumn = reportHasDetailPercents(allRules);
  const imageId = registerLogoImage(workbook);
  const usedSheetNames = new Set<string>();
  for (const group of report.groups) {
    addKiHybridSheet(
      workbook, report, group, checkType, detailColumns, showCountColumn, showStepPercentColumn,
      imageId, usedSheetNames,
    );
  }
  return workbook;
}

/** Alle Prüfregeln einer Datei, unabhängig davon, ob sie direkt oder unter Positionen liegen. */
function allRulesOf(group: ReportGroup): ReportRule[] {
  return (group.structures?.length ?? 0) > 0
    ? group.structures!.flatMap(structure => structure.rules ?? [])
    : group.rules ?? [];
}

/** Alphabetisch (numerisch-bewusst) nach Kürzel, wie die Spaltenansicht (siehe report-leaves.ts). */
function compareRulesByShortCode(a: ReportRule, b: ReportRule): number {
  return (a.shortCode ?? '').localeCompare(b.shortCode ?? '', 'de', {numeric: true})
    || a.name.localeCompare(b.name, 'de', {numeric: true});
}

export async function downloadKiHybridExcelReport(
  report: NormalizedReport,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
  runName?: string | null,
): Promise<void> {
  const workbook = await buildKiHybridReportWorkbook(report, config, checkType);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {type: EXCEL_MIME_TYPE});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = reportExportFileName(checkType, runName, report.meta.rulesetName, report.meta.checkDate, 'xlsx');
  link.click();
  URL.revokeObjectURL(url);
}

const STATUS_LABELS: Record<ReportStatus, string> = {
  success: 'Erfüllt', warning: 'Teilweise erfüllt', error: 'Nicht erfüllt', unknown: 'Unbekannt',
};

function statusFontColor(status: ReportStatus): string {
  switch (status) {
    case 'success':
      return CI_COLORS.turquoise;
    case 'warning':
      return CI_COLORS.orange;
    case 'error':
      return CI_COLORS.red;
    case 'unknown':
      return CI_COLORS.gray;
  }
}

/**
 * Spalten der Ergebnis-Detailtabelle: konfigurierte Spalten (siehe Berichtskonfiguration), sonst
 * alle tatsächlich vorkommenden KI-Ausgabefelder (in Reihenfolge des ersten Auftretens), damit ein
 * Altbericht ohne Konfiguration trotzdem nichts verliert.
 */
function resolveGenericDetailColumns(
  rules: ReadonlyArray<ReportRule>, config: ReportDisplayConfig | null | undefined,
): DetailColumn[] {
  const configured = resolveDetailColumns(config);
  if (configured.length > 0) return configured;

  const seen = new Set<string>();
  const fallback: DetailColumn[] = [];
  for (const rule of rules) {
    for (const detail of rule.details ?? []) {
      for (const key of Object.keys(detail.fields ?? {})) {
        const lower = key.toLowerCase();
        if (seen.has(lower)) continue;
        seen.add(lower);
        fallback.push({key, label: key});
      }
    }
  }
  return fallback;
}

/** Ob irgendeine der Regeln eine Bauteilanzahl liefert (sonst bleibt die Spalte weg). */
function reportHasElementCounts(rules: ReadonlyArray<ReportRule>): boolean {
  return rules.some(rule => (rule.details ?? []).some(detail => detail.count > 0));
}

/**
 * Ob irgendein Einzelergebnis einen eigenen Prozentwert liefert (sonst bleibt die Spalte weg).
 *
 * Trifft auf Facetten-Zeilen zu – IDS-Anforderungen innerhalb eines gemischten Berichts und die
 * Regelergebnisse des BIM-Basis-Checks. Prompt-basierte Prüfschritte haben keinen eigenen
 * Prozentwert, dort bleibt die Tabelle unverändert.
 */
function reportHasDetailPercents(rules: ReadonlyArray<ReportRule>): boolean {
  return rules.some(rule => (rule.details ?? []).some(detail => detail.percent != null));
}

/**
 * Entfernt Spalten, für die unter den gegebenen Regeln kein einziger Wert aufgelöst werden kann
 * (z. B. Verdikt-Felder, die laut `resolveDetailEntries` nie als Text erscheinen). Eine Spalte, die
 * durchgängig leer wäre, ist keine Information, sondern nur Rauschen in der Tabelle.
 */
function pruneEmptyDetailColumns(rules: ReadonlyArray<ReportRule>, columns: DetailColumn[]): DetailColumn[] {
  if (columns.length === 0) return columns;
  const keysWithValue = new Set<string>();
  for (const rule of rules) {
    for (const detail of rule.details ?? []) {
      for (const entry of resolveDetailEntries(detail, columns)) {
        if (entry.value) keysWithValue.add(entry.key.toLowerCase());
      }
    }
  }
  return columns.filter(col => keysWithValue.has(col.key.toLowerCase()));
}

/** Freitext-Ausgabefelder (z. B. „Antwort") bekommen mehr Breite, damit sie weniger Zeilen umbrechen. */
const ANSWER_LIKE_FIELD_RE = /^(antwort|answer|begr[üu]ndung|erl[äa]uterung|freitext|handlungsempfehlung)$/i;

/** Spalten-Definition einer Tabellenspalte des Blatts (fest oder dynamisch aus Ausgabeparametern). */
interface SheetColumn {
  key: string;
  label: string;
  width: number;
}

function addKiHybridSheet(
  workbook: ExcelJS.Workbook,
  report: NormalizedReport,
  group: ReportGroup,
  checkType: string | null | undefined,
  detailColumns: DetailColumn[],
  showCountColumn: boolean,
  showStepPercentColumn: boolean,
  imageId: number,
  usedSheetNames: Set<string>,
): void {
  const sheet = workbook.addWorksheet(sanitizeSheetName(group.name, usedSheetNames));
  const hasStructure = (group.structures?.length ?? 0) > 0;

  const columns: SheetColumn[] = [
    ...(hasStructure ? [{key: 'structure', label: 'Position', width: 24}] : []),
    {key: 'rule', label: 'Prüfregel', width: 30},
    {key: 'shortCode', label: 'Kürzel', width: 13},
    {key: 'description', label: 'Beschreibung', width: 34},
    {key: 'rulePercent', label: 'Ergebnis', width: 11},
    {key: 'ruleStatus', label: 'Status', width: 16},
    {key: 'stepHeading', label: 'Prüfschritt', width: 32},
    {key: 'stepStatus', label: 'Status Prüfschritt', width: 17},
    ...(showStepPercentColumn ? [{key: 'stepPercent', label: 'Ergebnis Prüfschritt', width: 13}] : []),
    ...(showCountColumn ? [{key: 'count', label: 'Bauteile', width: 11}] : []),
    ...detailColumns.map(col => ({
      key: `field:${col.key}`,
      label: col.label || col.key,
      width: ANSWER_LIKE_FIELD_RE.test(col.key) ? 55 : 24,
    })),
  ];
  const columnCount = columns.length;
  const colIndex: Record<string, number> = {};
  columns.forEach((col, i) => colIndex[col.key] = i + 1);

  sheet.columns = columns.map(col => ({width: col.width}));

  writeKiHybridTitleBlock(sheet, columnCount, checkType);
  placeLogoInTitleRow(sheet, columnCount, imageId);
  writeKiHybridMetaBlock(sheet, report, group);

  const headerRow = 10;
  columns.forEach(col => styleFlatHeaderCell(sheet.getCell(headerRow, colIndex[col.key]), col.label));
  sheet.getRow(headerRow).height = 26;
  sheet.views = [{state: 'frozen', ySplit: headerRow, showGridLines: false}];

  const structures: ReadonlyArray<Pick<ReportStructure, 'name' | 'rules'>> = hasStructure
    ? group.structures!
    : [{name: '', rules: group.rules ?? []}];

  let rowIdx = headerRow + 1;
  let ruleBandIndex = 0;
  let wroteAnyRule = false;

  for (const structure of structures) {
    const structureStartRow = rowIdx;
    const sortedRules = [...(structure.rules ?? [])].sort(compareRulesByShortCode);
    for (const rule of sortedRules) {
      wroteAnyRule = true;
      const band = ruleBandIndex % 2 === 1;
      ruleBandIndex++;
      const ruleStartRow = rowIdx;
      const details = (rule.details?.length ?? 0) > 0 ? rule.details : [null];

      for (const detail of details) {
        writeStepRow(sheet, rowIdx, colIndex, detail, detailColumns, band);
        rowIdx++;
      }

      const ruleEndRow = rowIdx - 1;
      writeRuleCells(sheet, ruleStartRow, colIndex, rule, band);
      if (ruleEndRow > ruleStartRow) {
        (['rule', 'shortCode', 'description', 'rulePercent', 'ruleStatus'] as const)
          .forEach(key => sheet.mergeCells(ruleStartRow, colIndex[key], ruleEndRow, colIndex[key]));
      }
    }

    if (hasStructure) {
      const structureEndRow = rowIdx - 1;
      const cell = sheet.getCell(structureStartRow, colIndex['structure']);
      cell.value = structure.name || '–';
      cell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: CI_COLORS.darkText}};
      cell.alignment = {horizontal: 'left', vertical: 'top', wrapText: true};
      cell.border = THIN_BORDER;
      if (structureEndRow > structureStartRow) {
        sheet.mergeCells(structureStartRow, colIndex['structure'], structureEndRow, colIndex['structure']);
      }
    }
  }

  if (!wroteAnyRule) {
    const lastRow = rowIdx;
    sheet.mergeCells(lastRow, 1, lastRow, columnCount);
    const cell = sheet.getCell(lastRow, 1);
    cell.value = 'Keine Prüfregeln vorhanden.';
    cell.font = {name: 'Segoe UI', size: 10, italic: true, color: {argb: CI_COLORS.darkText}};
    rowIdx++;
  }

  setupPrintLayout(sheet, rowIdx - 1, columnCount);
}

/** Zellen einer Prüfschritt-Zeile (Prüfschritt-Spalten + dynamische Ausgabefelder). */
function writeStepRow(
  sheet: ExcelJS.Worksheet,
  rowIdx: number,
  colIndex: Record<string, number>,
  detail: ReportDetail | null,
  detailColumns: DetailColumn[],
  band: boolean,
): void {
  const entries: ResolvedDetailEntry[] = detail ? resolveDetailEntries(detail, detailColumns) : [];
  const entryValue = (key: string) => entries.find(e => e.key.toLowerCase() === key.toLowerCase())?.value ?? '';

  const heading = detail
    ? (detail.heading || [detail.facetLabel, detail.name].filter(Boolean).join(': ') || '–')
    : '–';
  setPlainTextCell(sheet.getCell(rowIdx, colIndex['stepHeading']), heading, band);

  if (detail) {
    setStatusTextCell(sheet.getCell(rowIdx, colIndex['stepStatus']), detail.type, band);
  } else {
    setPlainTextCell(sheet.getCell(rowIdx, colIndex['stepStatus']), '–', band);
  }

  if (colIndex['stepPercent']) {
    if (detail && detail.percent != null) {
      setPercentCellColoredByStatus(sheet.getCell(rowIdx, colIndex['stepPercent']), detail.percent, detail.type, band);
    } else {
      // Kein eigener Prozentwert (Prüfschritt statt Facette) – leer statt „0 %", das ein Ergebnis behauptete.
      setPlainTextCell(sheet.getCell(rowIdx, colIndex['stepPercent']), '', band, {align: 'center'});
    }
  }

  if (colIndex['count']) {
    setPlainTextCell(
      sheet.getCell(rowIdx, colIndex['count']), detail && detail.count > 0 ? String(detail.count) : '', band,
      {align: 'center'},
    );
  }

  for (const col of detailColumns) {
    setPlainTextCell(sheet.getCell(rowIdx, colIndex[`field:${col.key}`]), entryValue(col.key), band);
  }
}

/** Über alle Prüfschritt-Zeilen der Regel verschmolzene Regel-Spalten. */
function writeRuleCells(
  sheet: ExcelJS.Worksheet,
  startRow: number,
  colIndex: Record<string, number>,
  rule: ReportRule,
  band: boolean,
): void {
  setPlainTextCell(sheet.getCell(startRow, colIndex['rule']), rule.name, band, {bold: true});
  setPlainTextCell(sheet.getCell(startRow, colIndex['shortCode']), rule.shortCode ?? '', band);
  setPlainTextCell(sheet.getCell(startRow, colIndex['description']), rule.description ?? '', band);
  setPercentCellColoredByStatus(sheet.getCell(startRow, colIndex['rulePercent']), rule.percent, rule.status, band);
  setStatusTextCell(sheet.getCell(startRow, colIndex['ruleStatus']), rule.status, band);
}

function setPlainTextCell(
  cell: ExcelJS.Cell, value: string, band: boolean, opts?: { bold?: boolean; align?: 'left' | 'center' },
): void {
  cell.value = value || '';
  cell.font = {name: 'Segoe UI', size: 10, bold: !!opts?.bold, color: {argb: CI_COLORS.darkText}};
  cell.alignment = {horizontal: opts?.align ?? 'left', vertical: 'top', wrapText: true};
  cell.border = THIN_BORDER;
  if (band) cell.fill = solidFill(CI_COLORS.lightGray);
}

function setStatusTextCell(cell: ExcelJS.Cell, status: ReportStatus, band: boolean): void {
  cell.value = STATUS_LABELS[status];
  cell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: statusFontColor(status)}};
  cell.alignment = {horizontal: 'center', vertical: 'middle'};
  cell.border = THIN_BORDER;
  if (band) cell.fill = solidFill(CI_COLORS.lightGray);
}

function setPercentCellColoredByStatus(
  cell: ExcelJS.Cell, percent: number | null | undefined, status: ReportStatus, band: boolean,
): void {
  if (percent == null) {
    setUnratedCell(cell, band);
    return;
  }
  const clamped = clampPercent(percent);
  cell.value = clamped / 100;
  cell.numFmt = '0%';
  cell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: statusFontColor(status)}};
  cell.alignment = {horizontal: 'center', vertical: 'middle'};
  cell.border = THIN_BORDER;
  if (band) cell.fill = solidFill(CI_COLORS.lightGray);
}

/** Ergebniszelle ohne Bewertung: „–" in Grau, damit sie nicht als 0 % gelesen wird. */
function setUnratedCell(cell: ExcelJS.Cell, band = false): void {
  cell.value = '–';
  cell.numFmt = 'General';
  cell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: CI_COLORS.gray}};
  cell.alignment = {horizontal: 'center', vertical: 'middle'};
  cell.border = THIN_BORDER;
  if (band) cell.fill = solidFill(CI_COLORS.lightGray);
}

function styleFlatHeaderCell(cell: ExcelJS.Cell, label: string): void {
  cell.value = label;
  cell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: CI_COLORS.white}};
  cell.fill = solidFill(CI_COLORS.darkBlue);
  cell.alignment = {horizontal: 'left', vertical: 'middle', wrapText: true};
  cell.border = THIN_BORDER;
}

function writeKiHybridTitleBlock(
  sheet: ExcelJS.Worksheet, columnCount: number, checkType: string | null | undefined,
): void {
  sheet.mergeCells(1, 1, 1, columnCount);
  const title = sheet.getCell(1, 1);
  title.value = reportTypeLabel(checkType);
  title.font = {name: 'Segoe UI', size: 20, bold: true, color: {argb: CI_COLORS.white}};
  title.fill = solidFill(CI_COLORS.darkBlue);
  title.alignment = {horizontal: 'left', vertical: 'middle'};
  sheet.getRow(1).height = 30;

  sheet.mergeCells(2, 1, 2, columnCount);
  const subtitle = sheet.getCell(2, 1);
  subtitle.value = reportTypeSubtitle(checkType);
  subtitle.font = {name: 'Segoe UI', size: 10, italic: true, color: {argb: CI_COLORS.darkBlue}};
  subtitle.fill = solidFill(CI_COLORS.turquoiseLight);
  subtitle.alignment = {horizontal: 'left', vertical: 'middle'};
  sheet.getRow(2).height = 18;
}

function writeKiHybridMetaBlock(
  sheet: ExcelJS.Worksheet, report: NormalizedReport, group: ReportGroup,
): void {
  // Bei Positionsstrukturen (Hybrid) hängen die Prüfregeln an den Positionen, nicht an der Datei
  // selbst – `group.rules` wäre dann immer 0.
  const rules = allRulesOf(group);
  const ruleCount = rules.length;
  const unratedRuleCount = rules.filter(rule => rule.percent == null).length;

  const metaRows: Array<{ label: string; value: string | number; numFmt?: string }> = [
    {label: 'Prüfung durchgeführt am:', value: formatDate(report.meta.checkDate)},
    {label: 'myCheck:', value: report.meta.rulesetName || '–'},
    {label: 'Prüfer*in:', value: report.meta.checkerName || '–'},
    {label: 'Geprüfte Datei:', value: group.name || '–'},
    {label: 'Geprüfte Prüfregeln:', value: ruleCount},
    {label: 'Gesamtergebnis:', ...percentCellValue(group.percent)},
    // Grundgesamtheit des Gesamtergebnisses: ohne sie ist „100 %" nicht von „100 % von zwei
    // bewerteten aus zwölf Regeln" zu unterscheiden. Nur zeigen, wenn wirklich etwas fehlt.
    ...(unratedRuleCount > 0
      ? [{
        label: 'Bewertete Ergebnisse:',
        value: `${ruleCount - unratedRuleCount} von ${ruleCount}`,
      }]
      : []),
  ];

  metaRows.forEach((row, idx) => {
    const rowIdx = 4 + idx;
    const labelCell = sheet.getCell(rowIdx, 1);
    labelCell.value = row.label;
    labelCell.font = {name: 'Segoe UI', size: 10, color: {argb: CI_COLORS.darkText}};

    const valueCell = sheet.getCell(rowIdx, 2);
    valueCell.value = row.value;
    valueCell.font = {name: 'Segoe UI', size: 10, bold: true, color: {argb: CI_COLORS.darkText}};
    valueCell.alignment = {horizontal: 'left', vertical: 'middle'};
    if (row.numFmt) valueCell.numFmt = row.numFmt;
  });
}
